import { Controller } from '@hotwired/stimulus';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
// A `disposition: attachment` redirect doesn't navigate the page away, so without this the
// only feedback for clicking an already-ready icon is the browser's own (often delayed)
// download indicator — nothing changes on the page itself in the meantime.
const DOWNLOAD_STARTING_MS = 2000;

export default class extends Controller {
  static values = {
    generateUrl: String,
    statusUrl: String,
    cancelUrl: String,
    downloadIconHtml: String,
    generatingTitle: String,
    generationStartedText: String,
    readyTitle: String,
    errorText: String,
    errorTimeoutText: String,
    errorWithReasonText: String,
    cancelTitle: String,
    // CSS selector of the select picking the previous year footprint the report compares with
    // (multiple_single_cf user flag): every request then carries that choice
    compareSelect: String,
  };

  connect() {
    this.originalHtml = this.element.innerHTML;
    this.state = 'idle';
    this.compareSelect?.addEventListener('change', this.comparisonChanged);
    this.checkInitialStatus();
  }

  disconnect() {
    this.stopPolling();
    this.clearDownloadStartingRevert();
    this.compareSelect?.removeEventListener('change', this.comparisonChanged);
  }

  get compareSelect() {
    return this.hasCompareSelectValue ? document.querySelector(this.compareSelectValue) : null;
  }

  // Each previous year footprint is a different report: drop what was shown for the old choice
  // and pick up the state of the new one
  comparisonChanged = () => {
    this.stopPolling();
    this.restore();
    this.checkInitialStatus();
  };

  withComparison(url) {
    const select = this.compareSelect;
    if (!select) return url;

    const withParam = new URL(url, window.location.origin);
    withParam.searchParams.set('compare_carbon_footprint_id', select.value);
    return withParam.toString();
  }

  // Restores whatever was in flight when the user last left this page (another tab, or a
  // reopened modal), so a generation started earlier is picked up instead of looking idle.
  async checkInitialStatus() {
    try {
      const response = await fetch(this.withComparison(this.statusUrlValue), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const data = await response.json();
      if (data.status === 'ready') {
        this.showReady(data.download_url);
      } else if (data.status === 'pending' || data.status === 'processing') {
        this.showGenerating();
        this.startPolling();
      }
    } catch {
      // Leave the button idle; the user can still trigger generation manually.
    }
  }

  async request(event) {
    event.preventDefault();

    if (this.state === 'generating') return;
    if (this.state === 'ready') {
      this.showDownloadStarting();
      window.location.assign(this.downloadUrl);
      return;
    }

    this.showGenerating();
    this.notifyGenerationStarted();

    try {
      const response = await fetch(this.withComparison(this.generateUrlValue), {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.status === 'ready') {
        this.showReady(data.download_url);
      } else {
        this.startPolling();
      }
    } catch {
      this.showError(null);
    }
  }

  startPolling() {
    // Idempotent: `checkInitialStatus()` and `request()` can both race to call this on the
    // same instance (e.g. a click landing before the initial status fetch resolves). Without
    // clearing first, the earlier interval's id is silently overwritten and leaked — it keeps
    // running forever with nothing able to reference/cancel it, re-firing `showError()` (and
    // therefore a new toast) on every poll indefinitely once the record is stuck `failed`.
    this.stopPolling();
    this.pollingStartedAt = Date.now();
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  async poll() {
    if (Date.now() - this.pollingStartedAt > POLL_TIMEOUT_MS) {
      this.showError('timeout');
      return;
    }

    try {
      const response = await fetch(this.withComparison(this.statusUrlValue), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.status === 'ready') {
        this.showReady(data.download_url);
      } else if (data.status === 'cancelled') {
        this.restore();
      } else if (data.status === 'failed' || data.status === 'none') {
        this.showError(data.reason);
      }
    } catch {
      this.showError(null);
    }
  }

  // Triggered by clicking the stop button revealed on hover over the spinner. Cancellation
  // isn't instantaneous (the worker may still be mid-render), so this stops the regular
  // polling and switches to a short, tighter poll of its own until the backend confirms the
  // record has actually reached `cancelled`, then restores the idle icon either way.
  async cancel(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.state !== 'generating') return;

    this.stopPolling();

    try {
      await fetch(this.withComparison(this.cancelUrlValue), {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: 'application/json',
        },
      });
    } catch {
      // The status poll below is the source of truth regardless of whether this request
      // itself succeeded.
    }

    this.pollUntilCancelled();
  }

  pollUntilCancelled(attempt = 0) {
    const MAX_ATTEMPTS = 5;

    fetch(this.withComparison(this.statusUrlValue), { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.status === 'cancelled' || attempt >= MAX_ATTEMPTS) {
          this.restore();
        } else {
          setTimeout(() => this.pollUntilCancelled(attempt + 1), 1000);
        }
      })
      .catch(() => this.restore());
  }

  notifyGenerationStarted() {
    this.notify(this.generationStartedTextValue, 'info');
  }

  // Reuses the same markup/CSS and `flash-message` Stimulus controller as the server-rendered
  // Rails flashes (`layouts/v2/_flash_messages.html.erb`), just inserted client-side into the
  // adjacent `#js-flash-messages` anchor so it looks and auto-dismisses identically. Unlike
  // `alert()`, this never blocks the page and multiple calls (e.g. from several report icons
  // on the same page) simply stack as toasts instead of queueing modal dialogs the user has
  // to dismiss one by one.
  notify(message, type) {
    const container = document.getElementById('js-flash-messages');
    if (!container) return;

    const banner = document.createElement('div');
    banner.dataset.controller = 'flash-message';
    banner.dataset.flashMessageAutoDismissableValue = 'true';
    banner.className = `alert alert-dismissible alert-${type}`;
    banner.innerHTML =
      '<div data-flash-message-target="content"></div>' +
      '<div class="close" data-dismiss="alert"><span>&times;</span></div>';
    banner.querySelector('[data-flash-message-target="content"]').textContent = message;

    container.appendChild(banner);
  }

  // Brief spinner on click for an already-ready report: the download redirect
  // (`detailed_report_download` → signed GCS URL) doesn't navigate the page away, so without
  // this the icon just sits there while the browser quietly starts the download.
  showDownloadStarting() {
    this.clearDownloadStartingRevert();
    this.element.classList.add('disable');
    this.element.innerHTML = '<span class="detailed-report-pdf-spinner" role="status"></span>';
    this.downloadStartingTimer = setTimeout(() => {
      this.downloadStartingTimer = null;
      this.showReady(this.downloadUrl);
    }, DOWNLOAD_STARTING_MS);
  }

  clearDownloadStartingRevert() {
    if (this.downloadStartingTimer) {
      clearTimeout(this.downloadStartingTimer);
      this.downloadStartingTimer = null;
    }
  }

  showGenerating() {
    this.state = 'generating';
    this.lockSize();
    this.element.classList.add('disable');
    this.element.title = this.generatingTitleValue;
    this.element.innerHTML =
      '<span class="detailed-report-pdf-generating">' +
      '<span class="detailed-report-pdf-spinner" role="status"></span>' +
      '<button type="button" class="detailed-report-pdf-cancel" ' +
      `title="${this.cancelTitleValue}" data-action="click->detailed-report-pdf#cancel">` +
      '<span class="detailed-report-pdf-cancel-icon"></span></button>' +
      '</span>';
  }

  showReady(downloadUrl) {
    this.stopPolling();
    this.state = 'ready';
    this.downloadUrl = downloadUrl;
    this.lockSize();
    this.element.classList.remove('disable');
    this.element.title = this.readyTitleValue;
    this.element.innerHTML = this.downloadIconHtmlValue;
  }

  // `reason` distinguishes why generation failed, so the user isn't left with the same
  // unhelpful generic message every time: `'timeout'` means the job was silently lost (e.g. a
  // hard-killed Sidekiq worker) rather than a real render error, and any other string is the
  // actual exception message captured server-side (CarbonFootprintReportPdf#error_message).
  showError(reason) {
    this.stopPolling();
    this.restore();
    this.notify(this.errorMessageFor(reason), 'danger');
  }

  errorMessageFor(reason) {
    if (reason === 'timeout') return this.errorTimeoutTextValue;
    if (reason) return this.errorWithReasonTextValue.replace('%{reason}', reason);

    return this.errorTextValue;
  }

  restore() {
    this.state = 'idle';
    this.sized = false;
    this.element.style.display = '';
    this.element.style.alignItems = '';
    this.element.style.justifyContent = '';
    this.element.style.width = '';
    this.element.style.height = '';
    this.element.removeAttribute('title');
    this.element.classList.remove('disable');
    this.element.innerHTML = this.originalHtml;
  }

  // Freezes the trigger's footprint to its idle size the first time we swap its content, so
  // the spinner and the later download icon don't reflow the surrounding layout.
  lockSize() {
    if (this.sized) return;

    const rect = this.element.getBoundingClientRect();
    this.element.style.width = `${rect.width}px`;
    this.element.style.height = `${rect.height}px`;
    this.element.style.display = 'inline-flex';
    this.element.style.alignItems = 'center';
    this.element.style.justifyContent = 'center';
    this.sized = true;
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
